import {view, type CssProperties} from '@sail/ui';
import {AnimatePresence, motion, tokens} from '@sail/motion';
import {isEmbedded} from 'src/utils/utils';
import {MEDIA_QUERIES} from 'src/v2/util/mediaQueries';
import {useSelector} from 'src/v2/state/store';
import {BackButton} from 'src/v2/components/common/BackButton';
import {PayTitle} from 'src/v2/components/screen/PayTitle';
import {SetupTitle} from 'src/v2/components/screen/SetupTitle';
import {useSessionContext} from 'src/state/usePayinSessionContext';

const embeddedStyles: {card: CssProperties} = {
  card: {
    boxShadow: 'none',
    borderStyle: 'none',
    width: 'fill',
    minWidth: undefined,
    paddingX: 'space.0',
    paddingY: 'space.0',
    [MEDIA_QUERIES.mediumWidth]: {
      paddingX: 'space.0',
      paddingY: 'space.0',
    },
  },
};

export const ScreenContentLayout = ({
  children,
  bottom,
  onBack,
  hideTitle,
}: {
  children: React.ReactNode;
  bottom?: React.ReactNode;
  onBack?: () => void;
  hideTitle?: boolean;
}) => {
  const embedded = isEmbedded();
  const {mode} = useSessionContext();
  const screen = useSelector((state) => state.screen);
  const title = mode === 'pay' ? <PayTitle /> : <SetupTitle />;
  const showTitle = !hideTitle && !embedded;

  return (
    <view.div
      css={{
        stack: 'y',
        width: '388px',
        minWidth: '388px',
        gapY: 'large',
        boxShadow:
          '0px -4px 32px 0px rgba(0, 0, 0, 0.04), 0px 12px 32px 0px rgba(0, 0, 0, 0.04)',
        borderRadius: '20px',
        borderWidth: 'small',
        borderStyle: 'solid',
        borderColor: 'form',
        paddingX: 'large',
        paddingY: 'xlarge',
        height: 'fit',
        [MEDIA_QUERIES.smallWidth]: {
          width: 'fill',
        },
        [MEDIA_QUERIES.mediumWidth]: {
          boxShadow: 'none',
          borderStyle: 'none',
          paddingX: '16px',
          paddingY: '16px',
        },
        [MEDIA_QUERIES.mediumWidthHeight]: {
          distribute: 'space-between',
          height: '100%',
        },
        ...(embedded && embeddedStyles.card),
      }}
    >
      <AnimatePresence mode="wait">
        <view.div
          key={screen}
          uses={[
            motion({
              initial: {opacity: 0, x: tokens.translate.regular},
              animate: {opacity: 1, x: 0},
              exit: {opacity: 0, x: -tokens.translate.regular},
              transition: tokens.transition.default,
            }),
          ]}
          css={{stack: 'y', gapY: 'large', height: 'fit'}}
        >
          {onBack ? (
            <view.div css={{stack: 'x', alignY: 'center'}}>
              <view.div css={{width: 'medium', height: 'medium'}}>
                <BackButton onBack={onBack} />
              </view.div>
              {showTitle ? (
                <>
                  <view.div css={{flex: '1', alignX: 'center'}}>
                    {title}
                  </view.div>
                  {/* Spacer matching back button width to keep title centered */}
                  <view.div css={{width: 'medium'}} />
                </>
              ) : null}
            </view.div>
          ) : (
            <>{showTitle ? title : null}</>
          )}
          {children}
        </view.div>
      </AnimatePresence>
      {bottom}
    </view.div>
  );
};
